import {
  SidebarPanelLayout,
  SidebarPanelHeader,
  SidebarPanelBody,
  SidebarPanelLeftSidebar,
  SidebarPanelContent,
} from "../SidebarPanelLayout";
import GradientTabButton from "@/lib/components/Button/GradientTabButton";
import { GoBack, MainViewState, ViewStateOf } from "../creative-project-hooks";
import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import { useUploadCreativeElementMutation } from "@/lib/queries/creative-element-queries";
import { useCallback, useState } from "react";
import { IconUpload, IconUser, IconUsers, IconX } from "@tabler/icons-react";
import LoadingSpinner from "@/lib/components/LoadingSpinner";
import {
  CreativeElementCategory,
  type CreativeElementDto,
} from "@repo/api/creative-element";
import { useDropzone } from "react-dropzone";
import CreativeElementsListItem from "./CreativeElementsListItem";
import CreativeElementsFilters from "./CreativeElementsFilters";
import { Button } from "@/lib/components/Button/Button";
import toast from "@/lib/components/Toast";
import {
  useElementsData,
  useElementsVirtualizer,
} from "./creative-elements-hooks";
import { ActionIdentifier } from "@/lib/stores/editor-store/creative-project-state";
import { emitInspirationAction } from "../Inspiration/inspiration-action-events";

const EMPTY_ELEMENT_IDS: string[] = [];
const MAX_SELECTED_ELEMENTS = 8;

export default function CreativeElementsView({
  mainView,
  goBackMainView,
}: {
  mainView: ViewStateOf<MainViewState, "ELEMENTS">;
  goBackMainView: GoBack;
}) {
  const [activeTab, setActiveTab] = useState<CreativeElementCategory>("HUMAN");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const teamExternalId = useEditorStore((state) => state.teamExternalId);

  const handleGoBack = () => {
    emitInspirationAction(ActionIdentifier.SELECT_ELEMENTS);
    goBackMainView();
  };

  const handleTabChange = useCallback((tab: CreativeElementCategory) => {
    setActiveTab(tab);
    setSelectedTags([]);
  }, []);

  const dynamicFormKey =
    mainView.sourceImageKey === "startSourceImageExternalId"
      ? ("startImageEditForm" as const)
      : ("endImageEditForm" as const);

  const selectedElementIds = useEditorStore(
    (state) =>
      state.service.getCurrentCreativeProjectState()?.[dynamicFormKey]
        ?.elementExternalIds ?? EMPTY_ELEMENT_IDS,
  );

  const { updateCurrentCreativeProjectState, storeCreativeElement } =
    useEditorStore((state) => state.service);

  const handleSelect = useCallback(
    (element: CreativeElementDto) => {
      storeCreativeElement(element);

      updateCurrentCreativeProjectState((draft) => {
        const form = draft[dynamicFormKey];
        if (!form) return;
        const idx = form.elementExternalIds.indexOf(element.externalId);
        if (idx >= 0) {
          form.elementExternalIds.splice(idx, 1);
        } else {
          if (form.elementExternalIds.length >= MAX_SELECTED_ELEMENTS) {
            toast.info(
              `You can select up to ${MAX_SELECTED_ELEMENTS} elements`,
            );
            return;
          }
          form.elementExternalIds.push(element.externalId);
        }
      });
    },
    [updateCurrentCreativeProjectState, storeCreativeElement, dynamicFormKey],
  );

  const handleClearSelection = useCallback(() => {
    updateCurrentCreativeProjectState((draft) => {
      const form = draft[dynamicFormKey];
      if (form) {
        form.elementExternalIds = [];
      }
    });
  }, [updateCurrentCreativeProjectState, dynamicFormKey]);

  const { mutate: uploadElements, isPending: isUploading } =
    useUploadCreativeElementMutation({
      teamExternalId,
      onSuccess: () => handleTabChange("OTHER"),
    });

  const handleDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (isUploading || acceptedFiles.length === 0) return;
      uploadElements(acceptedFiles);
    },
    [isUploading, uploadElements],
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    disabled: isUploading,
    noClick: true,
    noKeyboard: true,
    multiple: true,
    accept: {
      "image/png": [".png"],
      "image/jpeg": [".jpg", ".jpeg"],
      "image/webp": [".webp"],
    },
    onDrop: handleDrop,
  });

  return (
    <SidebarPanelLayout>
      <SidebarPanelHeader title="Select elements" onBackClick={handleGoBack} />
      <SidebarPanelBody>
        <SidebarPanelLeftSidebar>
          <ElementsTabs
            activeTab={activeTab}
            onTabChange={handleTabChange}
            onUploadClick={open}
            isUploading={isUploading}
          />
          <CreativeElementsFilters
            activeTab={activeTab}
            selectedTags={selectedTags}
            onSelectedTagsChange={setSelectedTags}
          />
        </SidebarPanelLeftSidebar>
        <SidebarPanelContent>
          <div className="relative min-h-0 flex-1" {...getRootProps()}>
            <input {...getInputProps()} />
            <ElementsList
              key={`${activeTab}-${selectedTags.join(",")}`}
              activeTab={activeTab}
              selectedElementIds={selectedElementIds}
              selectedTags={selectedTags}
              onSelect={handleSelect}
            />
            {(isUploading || isDragActive) && (
              <DropzoneOverlay isUploading={isUploading} />
            )}
          </div>
          <ElementsBottomBar
            selectedCount={selectedElementIds.length}
            onClear={handleClearSelection}
            onContinue={handleGoBack}
          />
        </SidebarPanelContent>
      </SidebarPanelBody>
    </SidebarPanelLayout>
  );
}

function ElementsTabs({
  activeTab,
  onTabChange,
  onUploadClick,
  isUploading,
}: {
  activeTab: CreativeElementCategory;
  onTabChange: (tab: CreativeElementCategory) => void;
  onUploadClick: () => void;
  isUploading: boolean;
}) {
  return (
    <div className="flex flex-col gap-3 border-b border-grey-200 p-6">
      <GradientTabButton
        icon={IconUser}
        label="Actors"
        isActive={activeTab === "HUMAN"}
        onClick={() => onTabChange("HUMAN")}
      />
      <GradientTabButton
        icon={IconUsers}
        label="Team elements"
        isActive={activeTab === "OTHER"}
        onClick={() => onTabChange("OTHER")}
      />
      <GradientTabButton
        icon={IconUpload}
        label={isUploading ? "Uploading..." : "Upload element"}
        isActive={false}
        onClick={onUploadClick}
        disabled={isUploading}
      />
    </div>
  );
}

function ElementsList({
  activeTab,
  selectedElementIds,
  selectedTags,
  onSelect,
}: {
  activeTab: CreativeElementCategory;
  selectedElementIds: string[];
  selectedTags: string[];
  onSelect: (element: CreativeElementDto) => void;
}) {
  const {
    elements,
    isPending,
    isError,
    isInVideo,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useElementsData({ activeTab, selectedElementIds, selectedTags });

  const {
    scrollContainerRef,
    columnsPerRow,
    totalRows,
    rowVirtualizer,
    virtualItems,
  } = useElementsVirtualizer({
    activeTab,
    elements,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  // Ugly IIFE to ensure div with scrollContainerRef is always rendered, no matter the state.
  const content = (() => {
    if (isPending) {
      return (
        <div className="flex h-full items-center justify-center">
          <LoadingSpinner size={24} />
        </div>
      );
    }

    if (isError) {
      return (
        <div className="flex h-full items-center justify-center px-8 py-12">
          <p className="text-sm font-medium text-text-secondary">
            Failed to load elements
          </p>
        </div>
      );
    }

    if (elements.length === 0) {
      return (
        <div className="flex h-full flex-col items-center justify-center px-8 py-12">
          <p className="text-sm font-medium text-text-secondary">No elements</p>
        </div>
      );
    }

    return (
      <div
        style={{
          height: `${rowVirtualizer.getTotalSize()}px`,
          width: "100%",
          position: "relative",
        }}
      >
        {virtualItems.map((virtualRow) => {
          const isLoaderRow = virtualRow.index >= totalRows;
          const startIndex = virtualRow.index * columnsPerRow;
          const rowElements = elements.slice(
            startIndex,
            startIndex + columnsPerRow,
          );

          return (
            <div
              key={virtualRow.index}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: `${virtualRow.size}px`,
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              {isLoaderRow ? (
                <div className="flex justify-center py-4">
                  {isFetchingNextPage && <LoadingSpinner size={20} />}
                </div>
              ) : (
                <div
                  className={
                    activeTab === "HUMAN"
                      ? "grid grid-cols-4 gap-3"
                      : "grid grid-cols-3 gap-3"
                  }
                >
                  {rowElements.map((element) => (
                    <CreativeElementsListItem
                      key={element.externalId}
                      element={element}
                      isSelected={selectedElementIds.includes(
                        element.externalId,
                      )}
                      isInVideo={isInVideo(element.externalId)}
                      onSelect={() => onSelect(element)}
                      activeTab={activeTab}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  })();

  return (
    <div
      ref={scrollContainerRef}
      className="absolute inset-0 overflow-y-auto px-5 py-4 pb-8"
    >
      {content}
    </div>
  );
}

function DropzoneOverlay({ isUploading }: { isUploading: boolean }) {
  return (
    <div
      className={`absolute inset-0 z-50 flex items-center justify-center rounded-br-xl ${
        isUploading ? "bg-white/95" : "bg-primary-100/95"
      }`}
    >
      {isUploading ? (
        <div className="flex items-center gap-2">
          <LoadingSpinner size={20} />
          <p className="text-sm font-medium text-text-primary">
            Uploading element...
          </p>
        </div>
      ) : (
        <div className="flex h-full w-full flex-col items-center justify-center rounded-br-xl border border-dashed border-primary-400">
          <IconUpload stroke={1.5} size={32} color="url(#icon-gradient)" />
          <h1 className="mt-4 text-base font-semibold">Drop file here</h1>
          <p className="text-sm text-text-secondary">
            to add it as a team element
          </p>
        </div>
      )}
    </div>
  );
}

function ElementsBottomBar({
  selectedCount,
  onClear,
  onContinue,
}: {
  selectedCount: number;
  onClear: () => void;
  onContinue: () => void;
}) {
  return (
    <div className="flex items-center justify-between border-t border-grey-200 px-8 py-4">
      <div className="flex flex-row items-center text-text-secondary">
        <Button
          variant="ghost"
          size="iconSm"
          onClick={onClear}
          className="-ml-3"
          disabled={selectedCount === 0}
        >
          <IconX strokeWidth={2} size={16} />
        </Button>
        <p className="text-sm font-normal">{selectedCount} selected</p>
      </div>
      <Button onClick={onContinue}>Continue</Button>
    </div>
  );
}
